<!--
  AppShell — wrapper raiz de uma página. Aplica o background padrão,
  insere o LangSwitcher e suporta slots opcionais (alerta topo, rodapé).

  Mantém duas variantes visuais compartilhando os mesmos tokens:
    - produto : páginas tipo OligoscanLanding / Clientes (dark premium com bg radial)
    - bio     : páginas tipo LinkBio / Depoimentos (dark simples, card central)

  Props:
    - variante          : 'produto' (default) | 'bio'
    - currentLang       : v-model do idioma ativo
    - langs             : lista de idiomas suportados (default: ['es','pt','en'])
    - mostrarSwitcher   : boolean (default true)
    - evitarBannerMobile: passa para LangSwitcher (default true qd há banner)
    - autoIdioma        : se true, ativa o composable useIdiomaAuto (default true)

  Eventos:
    - update:currentLang : ao trocar idioma
    - lang-change        : quando user muda manualmente (flag de "escolha manual")

  Slots:
    - alerta   : ex: <AlertaTopo>...</AlertaTopo>
    - default  : conteúdo da página
    - rodape   : ex: <RodapeSite />
-->
<template>
  <div :class="['app-shell', `app-shell--${variante}`]">
    <LangSwitcher
      v-if="mostrarSwitcher"
      :model-value="currentLang"
      :idiomas="langs"
      :evitar-banner-mobile="evitarBannerMobile"
      @update:model-value="onLangUpdate"
      @change="onLangChange"
    />

    <slot name="alerta" />

    <main class="app-shell-main">
      <slot />
    </main>

    <slot name="rodape" />
  </div>
</template>

<script setup>
import { ref, toRef } from 'vue'
import LangSwitcher from '@/components/global/idioma/LangSwitcher.vue'
import { useIdiomaAuto } from '@/composables/useIdiomaAuto'

const props = defineProps({
  variante: {
    type: String,
    default: 'produto',
    validator: (v) => ['produto', 'bio'].includes(v)
  },
  currentLang: { type: String, default: 'es' },
  langs: { type: Array, default: () => ['es', 'pt', 'en'] },
  mostrarSwitcher: { type: Boolean, default: true },
  evitarBannerMobile: { type: Boolean, default: true },
  autoIdioma: { type: Boolean, default: true }
})

const emit = defineEmits(['update:currentLang', 'lang-change'])

const langManualmenteEscolhido = ref(false)

const onLangUpdate = (v) => emit('update:currentLang', v)

const onLangChange = (v) => {
  langManualmenteEscolhido.value = true
  emit('lang-change', v)
}

if (props.autoIdioma) {
  useIdiomaAuto(
    {
      get value() { return props.currentLang },
      set value(v) { emit('update:currentLang', v) }
    },
    langManualmenteEscolhido,
    { idiomas: props.langs }
  )
}
</script>

<style scoped>
.app-shell {
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  font-family: var(--ds-font-body);
  color: var(--ds-text);
  background-color: var(--ds-bg);
}

.app-shell--produto {
  background-image: var(--ds-gradient-hero-bg);
}

.app-shell--bio {
  background-image: none;
}

.app-shell-main {
  display: block;
}

/* Bio usa layout vertical centralizado */
.app-shell--bio .app-shell-main {
  display: flex;
  justify-content: center;
  padding: 40px var(--ds-s-5);
}
</style>
